import React from 'react';
import { Link } from 'react-router-dom';
import { formatTimeOnly, formatDayOnly } from './dateFormatter';
import { TickerIcon } from '../TickerIcon';
import { getEventPagePath } from '../../utils/eventPaths';

interface NewsEvent {
  id: string;
  ts: string;
  ticker?: string;
  source?: string;
  title: string;
  summary?: string;
  link?: string;
  industry?: string;
  is_negative?: boolean;
  is_matched?: boolean; 
  signal_change_pct?: number | null;
}

interface EventCardProps {
  event: NewsEvent;
}

export const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const isMatched = event.is_matched === true;

  return (
    <div 
      className="group flex gap-6 py-6 border-b border-gray-100 dark:border-cat-border hover:bg-gray-50/50 dark:hover:bg-white/[0.01] transition-colors -mx-6 px-6"
    >
      {/* שמאל: תאריך וזמן */}
      <div className="w-20 shrink-0 flex flex-col gap-1">
        <span className="text-[10px] text-gray-500 dark:text-cat-text-muted font-bold uppercase tracking-wider">
          {formatDayOnly(event.ts)}
        </span>
        <span className="text-2xl text-gray-800 dark:text-cat-text-main font-light tracking-wide">
          {formatTimeOnly(event.ts)}
        </span>
        <div className="mt-2 text-[9px] text-gray-400 dark:text-cat-text-muted uppercase tracking-widest font-cat-mono flex flex-col gap-1 w-full">
          <span>{event.source || 'FINNHUB'} •</span>
          <span className="text-gray-400 dark:text-cat-text-dark" title={event.id}>
            {event.id ? `EVT_${event.id.slice(-4)}` : 'EVT_1029'}
          </span>
        </div>
      </div>

      {/* מרכז: תוכן הכתבה */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-3 mb-1.5">
          {event.ticker && <TickerIcon ticker={event.ticker} size="sm" />}
          <span className="text-sm font-bold text-gray-900 dark:text-cat-text-main uppercase">
            {event.ticker || 'UNKNOWN'}
          </span>
          {(event as any).industry && (
            <span className="text-[11px] text-gray-500 dark:text-cat-text-muted">
              {(event as any).industry}
            </span>
          )}
          
          {isMatched ? (
            <span className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest uppercase text-cat-green bg-cat-green/10 border border-cat-green/20 px-2 py-0.5 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-cat-green"></span> 
              Matched to signal
            </span>
          ) : (
            <span className="text-[10px] font-mono text-gray-400 dark:text-cat-text-muted uppercase tracking-wider">
              · No scanner hit
            </span>
          )}

          {event.is_negative && (
            <span className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest uppercase text-red-400 bg-red-900/20 border border-red-800/40 px-2 py-0.5 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span> Negative
            </span>
          )}
        </div>

        <h3 className="text-[15px] font-sans font-medium text-gray-900 dark:text-cat-text-main leading-snug mb-2 pr-4 truncate block" title={event.title}>
          <Link to={getEventPagePath(event.id)} className="hover:text-cat-accent transition-colors">
            {event.title}
          </Link>
        </h3>
        {event.summary && (
          <p className="text-[13px] font-sans text-gray-500 dark:text-cat-text-muted leading-relaxed line-clamp-2 pr-4 mb-3">
            {event.summary}
          </p>
        )}

        <div className="flex items-center gap-4 text-[11px] font-medium text-cat-accent mt-auto">
          <Link
            to={getEventPagePath(event.id)}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Read article
          </Link>
        </div>
      </div>

      {/* צד ימין */}
      <div className="hidden xl:flex w-44 shrink-0 flex-col items-end justify-center pr-2 font-mono">
        {isMatched ? (
          <div className="w-full flex flex-col items-start bg-green-50 dark:bg-cat-green/5 border border-green-200 dark:border-cat-green/30 rounded-xl p-3 transition-colors group-hover:border-green-300 dark:group-hover:border-cat-green/50">
            <span className="text-[9px] uppercase tracking-widest font-bold text-green-500 mb-2">Linked Signal</span>
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-gray-900 dark:text-cat-text-main font-bold text-sm">{event.ticker}</span>
              
              {event.signal_change_pct !== null && event.signal_change_pct !== undefined && (
                <>
                  <span className="text-gray-400 dark:text-cat-text-muted font-bold text-sm">·</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-cat-text-main tracking-wide">
                    {event.signal_change_pct >= 0 ? '+' : ''}
                    {event.signal_change_pct.toFixed(2)}%
                  </span>
                </>
              )}
            </div>
            <a 
              href={`/signal/${event.ticker}`} 
              className="text-[11px] text-cat-accent hover:opacity-80 transition-opacity"
            >
              Open signal →
            </a>
          </div>
          
        ) : (
          <span className="text-gray-400 dark:text-cat-text-muted uppercase tracking-wider text-right w-full pr-1 font-bold text-[9px]">
            No matching signal in window
          </span>
        )}
      </div>
    </div>
  );
};
